import { ChangeEvent, useState } from 'react';
import { Pagination } from 'antd';
import { SortDirectionEnum } from '@legacy/shared-types/enums/sort-direction.enum';
import { useProductsList } from '@queries/products/useProductsList/useProductsList';
import { Table } from '@components/common/Table/Table';
import { TableBody } from '@components/common/Table/TableBody/TableBody';
import { TableCell } from '@components/common/Table/TableCell/TableCell';
import { TableContainer } from '@components/common/Table/TableContainer/TableContainer';
import { TableHead } from '@components/common/Table/TableHead/TableHead';
import { TableRow } from '@components/common/Table/TableRow/TableRow';
import { ProductsToolbar } from '@pages/ProductsPage/ProductsTable/ProductsToolbar/ProductsToolbar';
import { ProductRow } from '@pages/ProductsPage/ProductsTable/ProductRow/ProductRow';
import { HeaderCell } from '@pages/ProductsPage/ProductsTable/HeaderCell/HeaderCell';
import { useProductSortAndFilters } from '@hooks/useProductSortAndFilters';
import { FiltersKeys, IProductFilters, tableColumnsNames, ALL_FILTER_OPTION, TableColumns } from './types';
import { FormattedMessage } from 'react-intl';
import { useDebounce } from '@hooks/useDebounce';
const FIRST_EMPTY_COLUMN = 1;
const ROWS_PER_PAGE = 100;

export const ProductsTable = () => {
  const { data: productList = [], isLoading } = useProductsList();
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.ASC);
  const [orderBy, setOrderBy] = useState<TableColumns>('isActive');
  const [searchedValue, setSearchedValue] = useState('');
  const debouncedSearchValue = useDebounce(searchedValue, 300);

  const [page, setPage] = useState(0);
  const [filtersState, setFiltersState] = useState<IProductFilters>({
    productCategory: ALL_FILTER_OPTION,
    ftv: ALL_FILTER_OPTION,
    productType: ALL_FILTER_OPTION,
    isActiveProductsOnly: false,
    productRange: ALL_FILTER_OPTION,
  });

  const filterHandler = useProductSortAndFilters(order, orderBy, debouncedSearchValue, filtersState);
  const sortedAndFilteredRows = filterHandler(productList);

  const setFiltersHandler = (key: FiltersKeys, value: string | number | boolean) => {
    setFiltersState(prevState => ({ ...prevState, [key]: value }));

    if (page > 0) setPage(0);
  };

  const columnSortHandler = (columnId: TableColumns) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === columnId;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(columnId);
  };

  const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchedValue(event.target.value);
    if (page > 0) setPage(0);
  };

  return (
    <>
      <ProductsToolbar
        onSearch={searchHandler}
        searchValue={searchedValue}
        productList={productList}
        onFiltersChange={setFiltersHandler}
      />

      <div className={'mb-2 w-full overflow-hidden rounded-sm bg-white shadow-sm'}>
        <TableContainer>
          <Table aria-label={'collapsible table'}>
            <TableHead>
              <TableRow>
                <TableCell />
                {tableColumnsNames.map(columnTextId => (
                  <HeaderCell
                    columnTextId={columnTextId}
                    order={order}
                    orderBy={orderBy}
                    key={columnTextId}
                    sortHandler={columnSortHandler}
                  />
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length + FIRST_EMPTY_COLUMN}>
              {sortedAndFilteredRows.length ? (
                sortedAndFilteredRows
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((row, index) => <ProductRow key={row.code} index={index} {...row} />)
              ) : (
                <TableRow>
                  <TableCell className={'text-center'} colSpan={9}>
                    <FormattedMessage id={'common.itemsNotFound'} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!!sortedAndFilteredRows.length && (
          <div className={'flex justify-end px-4 py-3'}>
            <Pagination
              current={page + 1}
              pageSize={ROWS_PER_PAGE}
              total={sortedAndFilteredRows.length}
              showSizeChanger={false}
              onChange={nextPage => setPage(nextPage - 1)}
            />
          </div>
        )}
      </div>
    </>
  );
};
